type TTokenKind = 'operand' | 'sign' | 'multiplication' | 'open' | 'close';

const TOKEN_REGEX = /\d+(?:[.,]\d+)?|[A-D]|[+\-−*/()]/g;
const PARAMETER_REGEX = /^[A-D]$/;
const ZERO_REGEX = /^0+(?:[.,]0+)?$/;

const SIGN_TOKENS = ['+', '-', '−'];
const MULTIPLICATION_TOKENS = ['*', '/'];

// какие виды токенов допустимы после каждого вида, start начало формулы
const ALLOWED_NEXT_KINDS: Record<TTokenKind | 'start', TTokenKind[]> = {
  start: ['operand', 'sign', 'open'],
  // после операнда нужен знак действия, иначе получится неявное умножение
  operand: ['sign', 'multiplication', 'close'],
  // после знака нельзя второй знак и нельзя закрыть скобку
  sign: ['operand', 'open'],
  multiplication: ['operand', 'open'],
  // знак после открывающей скобки допустим только как унарный, закрыть сразу нельзя
  open: ['operand', 'sign', 'open'],
  close: ['sign', 'multiplication', 'close'],
};

const FINAL_KINDS: TTokenKind[] = ['operand', 'close'];

const getTokenKind = (token: string): TTokenKind => {
  if (token === '(') return 'open';
  if (token === ')') return 'close';
  if (SIGN_TOKENS.includes(token)) return 'sign';
  if (MULTIPLICATION_TOKENS.includes(token)) return 'multiplication';

  return 'operand';
};

const getIsSequenceValid = (kinds: TTokenKind[]): boolean => {
  const isEveryTransitionAllowed = kinds.every((kind, index) =>
    ALLOWED_NEXT_KINDS[index === 0 ? 'start' : kinds[index - 1]].includes(kind),
  );

  return isEveryTransitionAllowed && FINAL_KINDS.includes(kinds[kinds.length - 1]);
};

const getIsBracketsBalanced = (tokens: string[]): boolean => {
  let openedBrackets = 0;

  for (const token of tokens) {
    openedBrackets += Number(token === '(') - Number(token === ')');
    if (openedBrackets < 0) return false;
  }

  return openedBrackets === 0;
};

const getHasRepeatedParameters = (tokens: string[]): boolean => {
  const parameters = tokens.filter((token) => PARAMETER_REGEX.test(token));

  return new Set(parameters).size !== parameters.length;
};

const getHasZeroMultiplication = (tokens: string[]): boolean =>
  tokens.some(
    (token, index) =>
      MULTIPLICATION_TOKENS.includes(token) &&
      (ZERO_REGEX.test(tokens[index - 1]) || ZERO_REGEX.test(tokens[index + 1])),
  );

// пробелы на формулу не влияют, регистр параметров приводим к верхнему
export const normalizeFormula = (formula: string): string => formula.replace(/\s/g, '').toUpperCase();

// формула разбирается на токены: число, параметр, знак действия или скобка
export const getIsFormulaValid = (formula: string): boolean => {
  const tokens = formula.match(TOKEN_REGEX) ?? [];

  // недопустимые символы не попадают в токены, поэтому склейка расходится с формулой
  if (tokens.join('') !== formula) return false;

  return (
    getIsSequenceValid(tokens.map(getTokenKind)) &&
    getIsBracketsBalanced(tokens) &&
    !getHasRepeatedParameters(tokens) &&
    !getHasZeroMultiplication(tokens)
  );
};
